// qr-generator-org-picker — searchable organization combobox.
//
// No shared combobox/autocomplete primitive exists yet in @jq4race/ui or
// apps/admin/src/shared (checked: the only related component is
// LocationPicker, a modal switcher with no text filtering). This is a small,
// module-local, accessible combobox built on the WAI-ARIA 1.2 combobox
// pattern (role="combobox" input + role="listbox" popup), filtering the
// caller-provided `options` client-side by case-insensitive substring match
// on `name`.
//
// Design choice: the input's displayed text is uncommitted state — typing
// only filters the dropdown and never calls `onChange`. `onChange(id)` fires
// only on an explicit commit (click or Enter on a highlighted option).
// Un-committed text is discarded on blur/Escape, reverting the input to the
// currently selected option's name (or empty). This avoids a prop-echo loop
// where a parent-driven `value` reset (e.g. clearing the field) would
// clobber text the user is still typing.

import * as React from 'react';

export interface OrgComboboxOption {
  id: string;
  name: string;
}

export interface OrgComboboxProps {
  id: string;
  /** Selected organization id, or '' when nothing is selected. */
  value: string;
  onChange: (id: string) => void;
  options: OrgComboboxOption[];
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  loadingLabel?: string;
  noResultsLabel: string;
  'aria-describedby'?: string;
}

export function OrgCombobox({
  id,
  value,
  onChange,
  options,
  loading = false,
  disabled = false,
  placeholder,
  loadingLabel,
  noResultsLabel,
  'aria-describedby': ariaDescribedBy,
}: OrgComboboxProps) {
  const selectedOption = options.find((o) => o.id === value) ?? null;
  const [query, setQuery] = React.useState(selectedOption?.name ?? '');
  const [open, setOpen] = React.useState(false);
  // -1 = nothing highlighted yet (fresh open) — the first ArrowDown moves to
  // index 0 rather than skipping it.
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q === (selectedOption?.name.toLowerCase() ?? '')) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query, selectedOption]);

  // Intentional "reset on change" effect — query/open aren't read in the
  // body, they're the trigger. Re-running on either keeps the highlight
  // cleared whenever the visible option set could have changed (retyping)
  // or the dropdown was just reopened.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [query, open]);

  React.useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeAndRevert();
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- closeAndRevert reads latest state via closure each render
  });

  function closeAndRevert() {
    setOpen(false);
    setQuery(selectedOption?.name ?? '');
  }

  function commitSelection(option: OrgComboboxOption) {
    onChange(option.id);
    setQuery(option.name);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, -1));
        break;
      case 'Enter': {
        e.preventDefault();
        const opt = filtered[highlightedIndex];
        if (opt) commitSelection(opt);
        break;
      }
      case 'Escape':
        e.preventDefault();
        closeAndRevert();
        break;
      default:
        break;
    }
  }

  const activeOption = filtered[highlightedIndex];

  return (
    <div ref={containerRef} className="relative">
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-describedby={ariaDescribedBy}
        aria-activedescendant={open && activeOption ? `${id}-option-${activeOption.id}` : undefined}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className="flex h-11 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition placeholder:text-jq-graphite/70 focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50"
      />
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm border border-jq-sand bg-jq-surface-0 py-1 shadow-md"
        >
          {loading ? (
            <div className="px-3 py-2 text-[13px] text-jq-graphite">{loadingLabel}</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-[13px] text-jq-graphite">{noResultsLabel}</div>
          ) : (
            filtered.map((option, index) => (
              // WAI-ARIA 1.2 combobox pattern — the text input keeps DOM
              // focus throughout; virtual focus on options is communicated
              // via aria-activedescendant on the input, so option elements
              // are intentionally not focusable themselves.
              // biome-ignore lint/a11y/useFocusableInteractive: see comment above
              <div
                key={option.id}
                id={`${id}-option-${option.id}`}
                role="option"
                aria-selected={option.id === value}
                onMouseDown={(e) => {
                  // Prevent the input's blur (which would close+revert)
                  // from firing before the click is registered.
                  e.preventDefault();
                  commitSelection(option);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={
                  index === highlightedIndex
                    ? 'cursor-pointer bg-jq-sand/40 px-3 py-2 text-[14px] text-jq-charcoal'
                    : 'cursor-pointer px-3 py-2 text-[14px] text-jq-charcoal hover:bg-jq-sand/20'
                }
              >
                {option.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
